import { useEffect, useState } from "react";
import {
  Alert,
  Backdrop,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Icon,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
} from "@mui/material";
import axios from "axios.js";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DataTable from "theme/Tables/DataTable";
import MDButton from "theme/components/MDButton";

function DataFormCard({ campaignRef, triggerRefresh, dataForms, campaignDataForms }) {
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [tableRows, setTableRows] = useState([]);
  const [selectedDataForm, setSelectedDataForm] = useState(null);
  const [openAttachDataFormDialog, setOpenAttachDataFormDialog] = useState(false);

  async function removeDataForm(ref) {
    setShowLoading(true);
    await axios
      .get(`/portal/campaigns/data_form/${ref}/delete.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function attachDataForm() {
    if (selectedDataForm === null) {
      setSeverity("error");
      setErrMsg("Select a data form");
      setOpen(true);
      return;
    }

    setOpenAttachDataFormDialog(false);
    setShowLoading(true);
    const json = {
      campaign: campaignRef,
      data_form: selectedDataForm,
    };

    await axios
      .post("portal/campaigns/data_form/attach.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const campaignDataFormsTableColumns = [
    { Header: "Data Form", accessor: "data_form", align: "left" },
    { Header: "Added On", accessor: "created", align: "right" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const buildCampaignDataFormsRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        data_form: row.title,
        actions: (
          <MDBox mt={1} variant="button">
            <MDButton
              size="small"
              variant="outlined"
              color="error"
              iconOnly
              onClick={() => {
                if (window.confirm(`Remove data form?`) === true) {
                  removeDataForm(row.reference);
                }
              }}
            >
              <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                delete
              </Icon>
            </MDButton>
          </MDBox>
        ),
        created: row.created,
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const cDataFormsRows = buildCampaignDataFormsRows(campaignDataForms);
    setTableRows(cDataFormsRows);
  }, [campaignDataForms]);
  return (
    <MDBox>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openAttachDataFormDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Attach Data Form</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDBox mt={2}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>Data Form</InputLabel>
                <Select
                  label="Data Form"
                  onChange={(e) => {
                    setSelectedDataForm(e.target.value);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  {
                    // --> Notice below changes
                    dataForms.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                      dataForms.map((record) => (
                        <MenuItem value={`${record.reference}`}>{record.title}</MenuItem>
                      ))
                  }
                </Select>
              </FormControl>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenAttachDataFormDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              attachDataForm();
            }}
          >
            Attach
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox mt={3}>
        <Card sx={{ height: "100%", marginTop: "0px" }}>
          <MDBox>
            <MDBox p={2}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" fontWeight="light">
                  <b>Data Form</b>
                  <br />
                  <small>
                    Capture required information during an activity. Attach a data form.
                  </small>
                </MDTypography>
                <MDButton
                  variant="contained"
                  color="dark"
                  iconOnly
                  onClick={() => {
                    setSelectedDataForm(null);
                    setOpenAttachDataFormDialog(true);
                  }}
                >
                  <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                </MDButton>
              </MDBox>
            </MDBox>
            <MDBox mb={3}>
              <DataTable
                table={{ columns: campaignDataFormsTableColumns, rows: tableRows }}
                isSorted
                entriesPerPage={false}
                showTotalEntries={false}
                buttons
                noEndBorder
                canSearch={false}
              />
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
    </MDBox>
  );
}

export default DataFormCard;
